import { IconTick } from "@/newComponents/icon/Icon";

import type { AddAlbumWizardStep } from "../store/addAlbumWizardStore";

import { useAddAlbumWizardStore } from "../store/addAlbumWizardStore";

// const stepConfig = {
//   "album-import": {
//     title: "Import Album",
//     description: "Connect and import from Spotify",
//   },
//   "select-songs": {
//     title: "Select Songs",
//     description: "Choose tracks to add to your catalogue",
//   },
//   "choose-writers": {
//     title: "Choose Writers",
//     description: "Select writers from your catalogue",
//   },
//   "assign-writers": {
//     title: "Assign Writers",
//     description: "Assign writers to each song",
//   },
//   "assign-shares": {
//     title: "Assign Shares",
//     description: "Set publishing shares for each writer",
//   },
// } as const;

const stepOrder: AddAlbumWizardStep[] = ["album-import", "select-songs", "choose-writers", "assign-writers", "details-and-shares", "summary-and-submit"];

type AddAlbumWizardStepProgressProps = {
  isTunecore?: boolean;
};

export default function AddAlbumWizardStepProgress({ isTunecore }: AddAlbumWizardStepProgressProps) {
  const { currentStep, completedSteps, canNavigateToStep, goToStep } = useAddAlbumWizardStore();

  const currentStepIndex = stepOrder.indexOf(currentStep);

  return (
    <div className="w-full mb-4">
      {/* Mobile Progress Indicator - shows only current step position */}
      <div className="block md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-medium ${
            isTunecore ? "text-white" : "text-gray-700"
          }`}
          >
            {currentStepIndex + 1}
            {" "}
            of
            {" "}
            {stepOrder.length}
          </span>

          {/* Step Dots - compact horizontal layout for mobile */}
          <div className="flex justify-center mt-3 space-x-2">
            {stepOrder.map((step, _index) => {
              const isActive = step === currentStep;
              const isCompleted = completedSteps.has(step);
              const canNavigate = canNavigateToStep(step);

              return (
                <button
                  key={step}
                  type="button"
                  disabled={!canNavigate}
                  onClick={() => goToStep(step)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    isCompleted
                      ? isTunecore
                        ? "bg-tunecore-green"
                        : "bg-sentric-teal"
                      : isActive
                        ? isTunecore
                          ? "bg-tunecore-blue"
                          : "bg-sentric-orange"
                        : canNavigate
                          ? isTunecore
                            ? "bg-tunecore-light-grey hover:bg-tunecore-orange"
                            : "bg-gray-300 hover:bg-sentric-orange"
                          : isTunecore
                            ? "bg-tunecore-light-grey"
                            : "bg-gray-200"
                  } ${canNavigate ? "cursor-pointer" : "cursor-not-allowed"}`}
                />
              );
            })}
          </div>
        </div>

        {/* Progress Bar */}
        <div className={`w-full h-2 rounded-full ${
          isTunecore ? "bg-tunecore-dark-element" : "bg-gray-200"
        }`}
        >
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              isTunecore ? "bg-tunecore-green" : "bg-sentric-orange"
            }`}
            style={{ width: `${((currentStepIndex + 1) / stepOrder.length) * 100}%` }}
          />
        </div>

      </div>

      {/* Desktop Progress Indicator - original horizontal layout */}
      <div className="items-center justify-between hidden md:flex">
        {stepOrder.map((step, index) => {
          const isActive = step === currentStep;
          const isCompleted = completedSteps.has(step);
          const canNavigate = canNavigateToStep(step);
          const isLastStep = index === stepOrder.length - 1;

          return (
            <div key={step} className={`flex items-center ${isLastStep ? "" : "flex-1"}`}>
              {/* Step Circle */}
              <div className="flex flex-col items-center">
                <button
                  type="button"
                  disabled={!canNavigate}
                  onClick={() => goToStep(step)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                    isCompleted
                      ? isTunecore
                        ? "bg-tunecore-green text-white"
                        : "bg-sentric-teal text-white hover:bg-sentric-teal/60"
                      : isActive
                        ? isTunecore
                          ? "bg-tunecore-blue text-white"
                          : "bg-sentric-orange text-white"
                        : canNavigate
                          ? isTunecore
                            ? "bg-tunecore-light-grey text-white hover:bg-tunecore-orange"
                            : "bg-sentric-orange/50 text-white hover:bg-sentric-orange hover:text-white"
                          : isTunecore
                            ? "bg-tunecore-dark-element text-gray-500"
                            : "bg-white text-black"
                  } ${canNavigate ? "cursor-pointer" : "cursor-not-allowed"}`}
                >
                  {isCompleted ? (
                    <IconTick className={`h-5 w-5 stroke-2 ${isTunecore ? "stroke-black" : "stroke-white"}`} />
                  ) : (
                    <span
                      style={{
                        // @ts-expect-error: text-box-trim is not yet in React's type definitions
                        textBoxTrim: "trim-end",
                      }}
                    >
                      {index + 1}
                    </span>
                  )}
                </button>
              </div>

              {/* Connector Line */}
              {!isLastStep && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  index < currentStepIndex
                    ? isTunecore ? "bg-tunecore-green" : "bg-green-500"
                    : isTunecore ? "bg-tunecore-light-grey" : "bg-gray-200"
                }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
